import type { User } from '@shady-pines/db';
import type { Awaitable, Uuid } from '@shady-pines/types';
import { isNonNil } from '@shady-pines/types';
import type { AuthChangeEvent } from '@supabase/supabase-js';
import { isNil } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useSupabase } from '../../hooks/use-supabase';
import { useUserStore } from './user-store';

const RelevantSessionChangeEvents: AuthChangeEvent[] = [
  'SIGNED_IN',
  'SIGNED_OUT',
  'INITIAL_SESSION',
];

export const useManageUser = (
  deviceType: 'windows' | 'android' | 'ios' | 'macos' | 'web',
  onSessionChange?: () => Awaitable<void>,
) => {
  const myProfile = useUserStore((s) => s.myProfile);
  const updateMyMetadata = useUserStore((s) => s.updateMyUserMetadata);
  const updateSession = useUserStore((s) => s.updateSession);
  const session = useUserStore((s) => s.session);
  const supabase = useSupabase();

  useEffect(() => {
    if (isNil(myProfile)) {
      return;
    }
    void updateMyMetadata(deviceType);
  }, [deviceType, myProfile, updateMyMetadata]);

  useEffect(() => {
    const initSession = async () => {
      console.log('[session] initSession');
      const {
        data: { session: existingSession },
      } = await supabase.auth.getSession();

      if (isNonNil(existingSession)) {
        await updateSession(existingSession);
      } else {
        console.log('[session] initSession: no existing session');
        const {
          data: { session: anonSession },
        } = await supabase.auth.signInAnonymously();
        console.log('[session] initSession: signed in anonymously');
        await updateSession(anonSession);
      }

      await onSessionChange?.();
    };

    void initSession();
  }, [onSessionChange, supabase.auth, updateSession]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      setTimeout(() => {
        void (async () => {
          console.log('session change', event);

          if (session?.access_token !== newSession?.access_token) {
            console.log('session changed acesss token so were doin some shit');
            await updateSession(newSession);
          }
          if (!RelevantSessionChangeEvents.includes(event)) {
            return;
          }

          if (isNil(newSession)) {
            console.log('[session] auth change hook: no existing session');
            const {
              data: { session: anonSession },
            } = await supabase.auth.signInAnonymously();
            console.log('[session] auth change hook: signed in anonymously');
            await updateSession(anonSession);
          }

          await updateSession(newSession);
          if (event === 'INITIAL_SESSION') {
            return;
          }
          await onSessionChange?.();
        })();
      }, 0);
    });

    return () => subscription.unsubscribe();
  }, [onSessionChange, session?.access_token, session?.user.email, supabase.auth, updateSession]);
};

export const useProfilesByIds = (profileIds: Uuid<User>[], skip?: boolean) => {
  const allFetched = useUserStore((s) => profileIds.every((p) => isNonNil(s.profilesById[p])));
  const loading = useUserStore((s) => profileIds.some((p) => s.loading.fetchingProfilesById[p]));
  const fetchProfilesById = useUserStore((s) => s.fetchProfilesById);
  const profiles = useUserStore((s) => profileIds.map((p) => s.profilesById[p]).filter(isNonNil));

  useEffect(() => {
    if (loading || allFetched || skip) {
      return;
    }

    void fetchProfilesById(profileIds);
  }, [allFetched, fetchProfilesById, loading, profileIds, skip]);

  return useMemo(
    () => ({
      loading,
      profiles,
    }),
    [loading, profiles],
  );
};

export const useUserSearch = (query: string) => {
  const searchUsers = useUserStore((s) => s.searchUsers);
  const loading = useUserStore((s) => s.loading.searchingUsers);
  const cacheEntry = useUserStore((s) => s.userSearchCache[query]);

  useEffect(() => {
    void searchUsers(query);
  }, [query, searchUsers]);

  return useMemo(() => {
    const users = cacheEntry?.users ?? [];
    const nextCursor = cacheEntry?.nextCursor ?? null;
    const hasMore = nextCursor !== null;
    const loadMore = () => {
      if (hasMore && !loading) {
        void searchUsers(query, nextCursor);
      }
    };
    return { users, loading, hasMore, loadMore };
  }, [cacheEntry, loading, query, searchUsers]);
};
